import { action, computed, IObservableArray, makeObservable, observable } from 'mobx';

import type { FilterData, OptionVariationData, PropertyData } from '../../data/model';

export class FilterPropertyState {
  private selection: FiltersSelectionState;
  public data: PropertyData;

  constructor(propertyData: PropertyData, selection: FiltersSelectionState) {
    makeObservable(this);
    this.data = propertyData;
    this.selection = selection;
  }

  @computed
  get selected() {
    return this.selection.properties.contains(this.data);
  }

  @action
  toggle() {
    this.selection.toggle(this.data);
  }

  public countAvailableResults(variations: Array<OptionVariationData>): number {
    const propertiesToConsider = [...this.selection.properties.filter((p) => p.name !== this.data.name), this.data];
    const valuesGroupsToConsider = Object.values(propertiesToConsider.groupBy<PropertyData>((p) => p.name)).map((x) =>
      x.map((i) => i.valueId),
    );
    const count = variations.filter((variation) =>
      valuesGroupsToConsider.every((valuesGroup) =>
        valuesGroup.intersection(variation.properties.map((p) => p.valueId)).any(),
      ),
    ).length;

    return count;
  }
}

export class FilterState {
  public data: FilterData;

  @observable.shallow
  availableProperties: Array<FilterPropertyState>;

  @computed
  public get selectedCount() {
    return this.availableProperties.filter((x) => x.selected).length;
  }

  constructor(data: FilterData, selection: FiltersSelectionState) {
    makeObservable(this);
    this.data = data;
    this.availableProperties = data.availableProperties.map((x) => new FilterPropertyState(x, selection));
  }
}

export class FiltersSelectionState {
  @observable.shallow
  public properties: Array<PropertyData> = [];

  constructor() {
    makeObservable(this);
  }

  public isSelected(property: PropertyData) {
    this.properties.contains(property);
  }

  @action
  public clear() {
    (this.properties as IObservableArray<PropertyData>).clear();
  }

  @action
  public toggle(property: PropertyData) {
    if (this.properties.contains(property)) {
      this.properties.remove(property);
    } else {
      this.properties.push(property);
    }
  }

  @action
  public deselect(property: PropertyData) {
    this.properties.remove(property);
  }
}
